import { ApiError } from "../services/api.errors";

/** details.code api.client stamps on an ApiError built from an sw.js offline stub. */
export const OFFLINE_ERROR_CODE = "SW_OFFLINE";

/**
 * Returns true when a caught error represents an unreachable backend —
 * either the device is offline (navigator.onLine === false) OR the fetch
 * itself failed with a network error (ERR_CONNECTION_REFUSED, ERR_NAME_NOT_RESOLVED,
 * ERR_INTERNET_DISCONNECTED, etc.). Both cases surface as TypeError("Failed to fetch")
 * in the browser, so we detect both with the same check.
 *
 * Use this instead of a bare `!navigator.onLine` in catch blocks so that
 * "backend process killed / server down" is treated the same as "no network".
 *
 * Never true for a 403: that is the server answering, so it proves we are online
 * even when navigator.onLine momentarily reads false. Without this, every offline
 * fallback gated on this helper had to remember to test isPermissionDenied first,
 * and the ones that forgot could serve cached PHI after a grant was revoked.
 */
export function isBackendUnreachable(error: unknown): boolean {
  if (isPermissionDenied(error)) return false;
  if (!navigator.onLine) return true;
  if (error instanceof TypeError && error.message === "Failed to fetch") return true;
  // With the service worker installed the TypeError above never reaches us: sw.js
  // networkOnly() catches it and returns a synthetic 503, so navigator.onLine
  // stays true and the native "offline" event never fires. api.client tags those
  // responses from the X-Kaagazy-Offline header the SW sets, which is the only
  // way to tell them apart from a real backend 503.
  return error instanceof ApiError && error.details.code === OFFLINE_ERROR_CODE;
}

/**
 * Returns true when a caught error is an authorization refusal — the server was
 * reached and answered "you may not do this".
 *
 * The distinction matters because a 403 is PROOF of connectivity, and several
 * catch blocks used to treat any failure as "the network died": they fell back to
 * the IndexedDB cache and dispatched a synthetic "offline" event, so revoking a
 * permission rendered stale PHI under a "You are offline" banner instead of
 * denying access. Check this FIRST in any catch that has an offline fallback.
 * isBackendUnreachable already returns false for a 403, but a fallback that is
 * not gated on it at all — a bare `.catch(() => readFromDexie())` — is only safe
 * with this check in front of it.
 */
export function isPermissionDenied(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === 403;
}

/** Generic fallback when a 403 arrived without a usable server message. */
const DENIED_FALLBACK = "You don't have permission to do this. Ask an admin to grant it.";

/**
 * Message to show the user for a failed action, preferring the server's reason
 * when it refused on authorization grounds.
 *
 * Every catch block used to report its own generic string — "Failed to delete
 * patient" — which is actively wrong for a 403: nothing failed, the action was
 * refused, and the user is left retrying something that can never succeed.
 * ``require_permission`` already sends a precise reason naming the missing
 * capability ("Your role does not include the 'Delete Patients' permission."),
 * and ``ClinicalCredentialRequiredError`` sends the KYC one — so surface that
 * instead of overwriting it.
 *
 *     toast.error(actionErrorMessage(err, "Failed to delete patient"));
 *
 * Non-403 failures keep the caller's fallback unchanged, so a real network or
 * server error still reads the way it did before.
 */
export function actionErrorMessage(error: unknown, fallback: string): string {
  if (!isPermissionDenied(error)) return fallback;
  const message = error instanceof ApiError ? error.message.trim() : "";
  // api.client falls back to `HTTP <status>` when the body carried no message;
  // that is not worth showing anyone.
  if (!message || /^HTTP \d+$/.test(message)) return DENIED_FALLBACK;
  return message;
}
